import {
    createOrganizationVariable,
    getOrganizationVariablesPageByOrganizationId,
    updateOrganizationVariable
} from '../../../../shared/api/controllers/organizationVariables';
import { SHARED_SUPPORT_ORGANIZATION_ID } from '../../../../shared/api/sparkFormSupportHistory';
import OrganizationVariable from '../../../../shared/models/organizations/OrganizationVariable';
import IResult from '../../../../shared/models/result/IResult';
import { PlatformHealthVariant } from './platformHealth';

export const DEFAULT_PLATFORM_HEALTH_THRESHOLD = 50;
export const PLATFORM_HEALTH_THRESHOLDS_VARIABLE_NAME = 'voicify_platform_health_thresholds_v1';
export const PLATFORM_HEALTH_THRESHOLDS_SCHEMA = 'voicify.platformHealthThresholds';

export interface PlatformHealthThresholdConfig {
    schema: typeof PLATFORM_HEALTH_THRESHOLDS_SCHEMA;
    version: 1;
    overrides: Record<PlatformHealthVariant, Record<string, number>>;
    updatedAt?: string;
    updatedBy?: string;
}

export interface LoadedPlatformHealthThresholdConfig {
    config: PlatformHealthThresholdConfig;
    variable?: OrganizationVariable;
}

export type PlatformHealthThresholdChanges = Record<string, number | null>;

export const createDefaultPlatformHealthThresholdConfig = (): PlatformHealthThresholdConfig => ({
    schema: PLATFORM_HEALTH_THRESHOLDS_SCHEMA,
    version: 1,
    overrides: {
        ordering: {},
        scheduling: {}
    }
});

export const normalizePlatformHealthThreshold = (value: unknown): number | null => {
    if (typeof value === 'string' && value.trim() === '') {
        return null;
    }
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric < 0 || numeric > 100) {
        return null;
    }
    return Math.round(numeric * 10) / 10;
};

const parseOverrides = (value: unknown): Record<string, number> | null => {
    if (value == null) return {};
    if (typeof value !== 'object' || Array.isArray(value)) return null;

    const parsed: Record<string, number> = {};
    for (const applicationId of Object.keys(value as Record<string, unknown>)) {
        const rawThreshold = (value as Record<string, unknown>)[applicationId];
        if (typeof rawThreshold !== 'number') return null;
        const threshold = normalizePlatformHealthThreshold(rawThreshold);
        if (!applicationId || threshold == null) return null;
        parsed[applicationId] = threshold;
    }
    return parsed;
};

export const parsePlatformHealthThresholdConfig = (value: string): PlatformHealthThresholdConfig | null => {
    try {
        const parsed = JSON.parse(value || '');
        if (
            parsed?.schema !== PLATFORM_HEALTH_THRESHOLDS_SCHEMA ||
            parsed?.version !== 1 ||
            typeof parsed?.overrides !== 'object' ||
            Array.isArray(parsed.overrides)
        ) {
            return null;
        }

        const ordering = parseOverrides(parsed.overrides.ordering);
        const scheduling = parseOverrides(parsed.overrides.scheduling);
        if (!ordering || !scheduling) return null;

        return {
            schema: PLATFORM_HEALTH_THRESHOLDS_SCHEMA,
            version: 1,
            overrides: { ordering, scheduling },
            updatedAt: typeof parsed.updatedAt === 'string' ? parsed.updatedAt : undefined,
            updatedBy: typeof parsed.updatedBy === 'string' ? parsed.updatedBy : undefined
        };
    } catch {
        return null;
    }
};

export const mergePlatformHealthThresholdChanges = (
    config: PlatformHealthThresholdConfig,
    variant: PlatformHealthVariant,
    changes: PlatformHealthThresholdChanges,
    updatedBy: string,
    updatedAt: string = new Date().toISOString()
): PlatformHealthThresholdConfig => {
    const overrides = {
        ordering: { ...config.overrides.ordering },
        scheduling: { ...config.overrides.scheduling }
    };

    Object.keys(changes).forEach(applicationId => {
        const value = changes[applicationId];
        if (value == null) {
            delete overrides[variant][applicationId];
            return;
        }

        const threshold = normalizePlatformHealthThreshold(value);
        if (threshold == null) {
            throw new RangeError('Thresholds must be between 0 and 100.');
        }
        overrides[variant][applicationId] = threshold;
    });

    return {
        schema: PLATFORM_HEALTH_THRESHOLDS_SCHEMA,
        version: 1,
        overrides,
        updatedAt,
        updatedBy: updatedBy.trim().toLowerCase()
    };
};

export const serializePlatformHealthThresholdConfig = (config: PlatformHealthThresholdConfig): string =>
    JSON.stringify(config);

const failureResult = <T>(resultType: IResult<any>['resultType'], errors: string[]): IResult<T> => ({
    data: null,
    resultType,
    errors
});

export const loadPlatformHealthThresholdConfig = async (): Promise<IResult<LoadedPlatformHealthThresholdConfig>> => {
    const result = await getOrganizationVariablesPageByOrganizationId(
        SHARED_SUPPORT_ORGANIZATION_ID,
        {
            skip: 0,
            take: 2,
            namePrefix: PLATFORM_HEALTH_THRESHOLDS_VARIABLE_NAME
        }
    );

    if (result.resultType !== 'Ok') {
        return failureResult(result.resultType, result.errors || ['Unable to load platform health thresholds.']);
    }

    const matchingVariables = (result.data || []).filter(variable =>
        variable.name === PLATFORM_HEALTH_THRESHOLDS_VARIABLE_NAME
    );
    if (matchingVariables.length === 0) {
        return {
            data: { config: createDefaultPlatformHealthThresholdConfig() },
            resultType: 'Ok',
            errors: []
        };
    }
    if (matchingVariables.length > 1) {
        return failureResult('Invalid', ['Multiple platform health threshold constants were found.']);
    }

    const variable = matchingVariables[0];
    const config = parsePlatformHealthThresholdConfig(variable.value);
    if (!config) {
        return failureResult('Invalid', ['The platform health threshold constant is malformed or uses an unsupported version.']);
    }

    return {
        data: { config, variable },
        resultType: 'Ok',
        errors: []
    };
};

export const savePlatformHealthThresholdChanges = async (
    variant: PlatformHealthVariant,
    changes: PlatformHealthThresholdChanges,
    updatedBy: string
): Promise<IResult<LoadedPlatformHealthThresholdConfig>> => {
    const latestResult = await loadPlatformHealthThresholdConfig();
    if (latestResult.resultType !== 'Ok' || !latestResult.data) {
        return failureResult(latestResult.resultType, latestResult.errors || ['Unable to refresh platform health thresholds.']);
    }

    let config: PlatformHealthThresholdConfig;
    try {
        config = mergePlatformHealthThresholdChanges(latestResult.data.config, variant, changes, updatedBy);
    } catch (error) {
        return failureResult('Invalid', [error?.message || 'The threshold changes are invalid.']);
    }

    const model: OrganizationVariable = {
        id: latestResult.data.variable?.id,
        name: PLATFORM_HEALTH_THRESHOLDS_VARIABLE_NAME,
        value: serializePlatformHealthThresholdConfig(config),
        organizationId: SHARED_SUPPORT_ORGANIZATION_ID
    };
    const saveResult = latestResult.data.variable?.id
        ? await updateOrganizationVariable(latestResult.data.variable.id, model)
        : await createOrganizationVariable(model);

    if (saveResult.resultType !== 'Ok') {
        return failureResult(saveResult.resultType, saveResult.errors || ['Unable to save platform health thresholds.']);
    }

    return {
        data: { config, variable: saveResult.data },
        resultType: 'Ok',
        errors: []
    };
};
